import {
  workspaceAccessTag,
  type PeopleGrantMode,
  type PeoplePerson,
} from "@playbookai/types";
import { PlaybookApiError } from "@playbookai/api-clients";

/**
 * SOLVE's words for the shared person field (assign-person d3, d40) and for
 * the two refusals a pick's save can meet. One place, so the greyed row, the
 * 403 under the field and the remove dialog all say "this Solve".
 */
export const SOLVE_PEOPLE_NOUN = "this Solve";
export const SOLVE_WHO_CAN_ADD_PEOPLE =
  "Only the owner or facilitator can add people";

const CANNOT_ADD_PEOPLE = `${SOLVE_WHO_CAN_ADD_PEOPLE} to ${SOLVE_PEOPLE_NOUN}.`;
const NO_LONGER_ELIGIBLE =
  "That person is no longer an active member of this workspace.";

/** `grant` for the Owner and the active Facilitator (`manage_members`). */
export function solvePeopleGrantMode(
  canManageMembers: boolean,
): PeopleGrantMode {
  return canManageMembers
    ? { kind: "grant", nounLabel: SOLVE_PEOPLE_NOUN, grantRoleLabel: "Viewer" }
    : {
        kind: "greyed",
        nounLabel: SOLVE_PEOPLE_NOUN,
        whoCanAddLabel: SOLVE_WHO_CAN_ADD_PEOPLE,
      };
}

/**
 * The text to show under a person field when its pick's save was refused for
 * the pick itself, or `null` for any other failure (which keeps its toast).
 */
export function personPickErrorMessage(error: unknown): string | null {
  if (!(error instanceof PlaybookApiError)) return null;
  if (error.envelope.code === "TARGET_NO_LONGER_ELIGIBLE") {
    return NO_LONGER_ELIGIBLE;
  }
  if (
    error.status === 403 &&
    error.envelope.code === "INSUFFICIENT_PRIVILEGE" &&
    error.envelope.details?.["requires"] === "manage_members"
  ) {
    return CANNOT_ADD_PEOPLE;
  }
  return null;
}

/**
 * The canvas roster as the person field shows it: people with access whose
 * name is already known. A member whose identity has not resolved yet is left
 * out rather than shown as an id; a search still finds them.
 */
export function solvePeopleRoster(
  members: readonly {
    readonly userId: string;
    readonly role: "owner" | "collaborator" | "viewer";
  }[],
  input: {
    readonly ownerId: string;
    readonly facilitatorId: string | null;
    readonly nameForUser: (userId: string) => string;
  },
): PeoplePerson[] {
  const roster: PeoplePerson[] = [];
  for (const member of members) {
    const name = input.nameForUser(member.userId);
    if (!name || name === member.userId) continue;
    roster.push({
      id: member.userId,
      name,
      access: workspaceAccessTag({
        role: member.userId === input.ownerId ? "owner" : member.role,
        isFacilitator: member.userId === input.facilitatorId,
      }),
    });
  }
  return roster;
}
